import * as React from 'react';
import styles from './EasterEgg.scss';
import equal from 'fast-deep-equal/react';
import { useDispatch } from 'react-redux';
import { POPUPS_NAME } from '~/components/PopupManager';
import classNames from 'classnames';
import PreCacheImage from '~/utils/preCacheImage';
import { isGoldShowcase } from '~/Layouts/Themes/SantaPage/settings';
import { Howl } from 'howler';
import { RootState, useAppSelector } from '~/Store';
import { appActions } from '~/Store/appSlice';
import { CategoryThemes } from '~/types/category';
import { SpinePlayer } from '@esotericsoftware/spine-player';
import SpineBackground from '~/components/Spine/SpineBackground';
import Account from '~/account/Account';
import { UserGraphicsQualityPresetTypes } from '@wg/web2clientapi/browser/getClientSettings';

const stateSelector = (state: RootState) => {
    return {
        gifts: state.account.gifts,
        currentPage: state.app.currentPage,
        categories: state.app.categories,
        isBlurView: state.app.isBlurView,
        clientSettings: state.account.clientSettings,
    };
};

const EasterEgg = () => {
    const dispatch = useDispatch();
    const { gifts, currentPage, categories, isBlurView, clientSettings } = useAppSelector(stateSelector, equal);
    const secretGift = gifts?.find((gift) => gift.isSecretGift);
    const timeout = React.useRef<NodeJS.Timeout>();
    const easterEggIconActive = React.useRef<HTMLDivElement>();
    const [spinePlayer, setSpinePlayer] = React.useState<SpinePlayer>(null);
    const [isStartedAnimation, toggleAnimationState] = React.useState(false);
    const howl = React.useMemo(() => {
        if (secretGift && secretGift.audioTrack?.mp3) {
            return new Howl({ src: secretGift?.audioTrack.mp3 });
        }
        return null;
    }, [secretGift]);

    const handleMouseEnter = () => {
        easterEggIconActive.current && easterEggIconActive.current.classList.add(styles.visible);
    };

    const handleMouseLeave = () => {
        easterEggIconActive.current && easterEggIconActive.current.classList.remove(styles.visible);
    };

    const handleClick = () => {
        if (!spinePlayer || isStartedAnimation) {
            return;
        }
        toggleAnimationState(true);
        if (howl) {
            howl.play();
        }
        const actionAnimation = spinePlayer.skeleton.data.animations.find((anim) => anim.name === 'action');
        spinePlayer.setAnimation('action', false);
        setTimeout(
            () => {
                dispatch(
                    appActions.changeVisibilityPopup({
                        name: POPUPS_NAME.GIFT,
                        isVisible: true,
                        data: { gift: secretGift, showRewards: true },
                    }),
                );
            },
            Math.round(actionAnimation.duration) * 1000,
        );
    };

    const isSanta = categories[currentPage?.name]?.theme === CategoryThemes.SANTA_PAGE_THEME;
    const isDisabledEaster = !secretGift || currentPage?.isBundlePage || !isSanta || (isSanta && isGoldShowcase(currentPage?.name)) || clientSettings?.preset === UserGraphicsQualityPresetTypes.LOW;

    React.useEffect(() => {
        return () => {
            clearTimeout(timeout.current);
        };
    }, []);

    if (isDisabledEaster) {
        return null;
    }

    PreCacheImage.add(secretGift.icons.innerBackground);

    const classesWrapper = classNames(styles.wrapper, {
        [styles.isBlur]: isBlurView,
        [styles.blackout]: isStartedAnimation,
    });

    return (
        <div className={classesWrapper}>
            <div className={styles.content}>
                <SpineBackground
                    spine={{
                        atlasUrl: 'https://wows-common-media.wgcdn.co/armory/1311/easter_egg_v6/Armory_Santa_Crates_Easter_Egg_animation_WOWs.atlas',
                        jsonUrl: 'https://wows-common-media.wgcdn.co/armory/1311/easter_egg_v6/Armory_Santa_Crates_Easter_Egg_animation_WOWs.json',
                    }}
                    containerId={'easter_egg'}
                    name="ny25_easter_egg"
                    viewportWidth={2560}
                    viewportHeight={1440}
                    setPlayer={setSpinePlayer}
                />
                {!isStartedAnimation && (
                    <>
                        <div className={styles.icon} />
                        <div
                            className={styles.hoverIcon}
                            ref={(_ref) => {
                                if (_ref) {
                                    easterEggIconActive.current = _ref;
                                }
                            }}
                        />
                    </>
                )}
                {!!Account.getAccount()?.id && !isStartedAnimation && spinePlayer && (
                    <svg className={styles.placeholderSvg} preserveAspectRatio="xMidYMid slice" fill="none" viewBox="0 0 2560 1440" xmlns="http://www.w3.org/2000/svg">
                        <path
                            onClick={handleClick}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            d="M2325.5 810.5L2331.5 806H2332L2342.5 804.5L2349 799L2348 794.5L2347 790L2348.5 785L2352 780L2353 775L2350 770.5L2345 770L2334.5 766.5L2329 761.5L2326.5 752L2321.5 749H2316.5C2316 749 2307 753 2306.5 753C2306.1 753 2300.33 754.667 2297.5 755.5L2288 753.5L2282 750L2274.5 743L2269.5 744.5L2265.5 749L2264.5 756.5C2264.17 759 2263.4 764.1 2263 764.5C2262.6 764.9 2261.17 770 2260.5 772.5L2262 778.5L2268 793L2266 803L2265 812L2266 820L2276.5 835V855.5L2281.5 867L2284 879L2310.5 886L2331.5 889H2355L2387 897V886L2395.5 869.5V858.5V843L2392.5 826L2389.5 824L2381 829H2372H2364.5L2355 826L2343.5 829L2329.5 826L2324.5 819L2325.5 810.5Z"
                        />
                    </svg>
                )}
            </div>
        </div>
    );
};

export default React.memo(EasterEgg);
